import * as Sentry from "@sentry/browser";
import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

// @ts-ignore
import { BrowserTracing } from "@sentry/tracing";

/**
 * Wrapper for dynamic imports that handles chunk loading failures.
 * When a new version is deployed, cached HTML may reference old chunk URLs that no longer exist.
 * This function will reload the page once to fetch fresh assets, preventing "Failed to fetch 
 * dynamically imported module" errors.
 */
function lazyWithRetry<T extends React.ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
    return lazy(async () => {
        const sessionStorageKey = "chunk_failed_reload";
        const hasReloaded = sessionStorage.getItem(sessionStorageKey) === "true";

        try {
            const component = await componentImport();
            // Clear the flag on successful load
            sessionStorage.removeItem(sessionStorageKey);
            return component;
        } catch (error) {
            // Only reload once to prevent infinite loops
            if (!hasReloaded) {
                sessionStorage.setItem(sessionStorageKey, "true");
                // Force a hard reload to bypass cache and get new HTML with updated chunk references
                window.location.reload();
                // Return a never-resolving promise to prevent rendering while reloading
                return new Promise(() => { });
            }
            // If we've already reloaded once, throw the error to show error boundary
            throw error;
        }
    });
}

const App = lazyWithRetry(() => import("./App").then(module => ({ default: module.App })));

const options = {
    api_host: import.meta.env.VITE_POSTHOG_HOST
}

const InitialLoadingIndicator = () => {
    const isDarkMode = React.useMemo(() => {
        const stored = localStorage.getItem("prefers-dark-mode");
        if (stored !== null) {
            return stored === "true";
        }
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }, []);

    return (
        <div style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDarkMode ? "#18181C" : "#ffffff",
            color: isDarkMode ? "white" : "#18181C",
            fontSize: "1rem",
            fontWeight: 600,
            zIndex: 10,
        }}>
            Loading...
        </div>
    );
};

if (process.env.NODE_ENV === "production") {
    Sentry.init({
        dsn: "https://71d97a93324e41888ae8aa9be10f31b2@o4504775215087616.ingest.sentry.io/4504775222165509",
        normalizeDepth: 8,
        integrations: [new BrowserTracing()],
        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: 0.1,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        beforeSend(event) {
            console.log("Sentry event", event);
            // // Check if it is an exception, and if so, show the report dialog
            // if (event.exception) {
            //     Sentry.showReportDialog({ eventId: event.event_id });
            // }
            return event;
        }
    });
}

const container = document.getElementById("root");
if (!container) {
    throw new Error("Root element not found");
}
const root = createRoot(container);
root.render(
    <React.StrictMode>
        <Suspense fallback={<InitialLoadingIndicator />}>
            <App />
        </Suspense>
    </React.StrictMode>
);
// root.render(
//     <React.StrictMode>
//         <PostHogProvider
//             apiKey={import.meta.env.VITE_POSTHOG_KEY}
//             options={options}
//         >
//             <App/>
//         </PostHogProvider>
//
//     </React.StrictMode>
// );
